import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { CreateUserDto } from "../../definitions/Landing";
import { createUser, sendConsent } from "../../actions/userActions";
import { divide } from "lodash";
import {
  Box,
  Button,
  Grid,
  InputLabel,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
} from "@mui/material";

function SignUpFormComponent({ consent, inputProps, buttonText, errorMessage, redirectUrl, createUser }) {
  const initialUser: CreateUserDto = {
    email: "",
    fornavn: "",
    efternavn: "",
    titel: "",
    stedadresse1: "",
    stedBy: "",
    stednavn1: "",
    stedtelefon: "",
    stedPostnr: "",
    userdefinedString1: "",
  };
  const [newUser, setNewUserValue] = useState<CreateUserDto>(initialUser);
  const [errorText, setErrorText] = useState<string>("");
  
  const validateField = (value: string, regex: string): boolean => {
    if (!regex || regex === "") return true;
    try {
      const regexPattern = new RegExp(regex);
      return regexPattern.test(value);
    } catch (e) {
      console.error("Invalid regex pattern:", regex, e);
      return true; // If regex is invalid, don't block the user
    }
  };

  const isFieldRequired = (field: any): boolean => {
    if (typeof field.required === 'boolean') {
      return field.required;
    }
    if (typeof field.required === 'string') {
      return field.required.toLowerCase() === 'true';
    }
    return false;
  };

  const handleSubmit = () => {
    // Check for required fields
    if(inputFields?.some((prop) => isFieldRequired(prop) && newUser[prop.name] == "")){
      setErrorText(errorMessage ?? "Please fill out all required fields");
      return;
    }
    
    // Check for regex validation
    const regexError = inputFields?.find((prop) => {
      if (prop.regex && newUser[prop.name] && !validateField(newUser[prop.name], prop.regex)) {
        return true;
      }
      return false;
    });
    
    if (regexError) {
      setErrorText(`${regexError.label} format is invalid`);
      return;
    }
    
    createUser(newUser);
  };
  const handleChange = (event) => {
    const { name, value } = event.target;
    setNewUserValue((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };
  useEffect(() => {
    if (consent) {
      window.location.href = redirectUrl;
    }
  }, [consent]);
  let inputFields: any[] | null = null;
  try {
    inputFields = JSON.parse(inputProps);
  } catch (e) {
    console.log(e);
  }
  return (
    <Box>
      {inputFields == null ? (
        <Typography color="error">JsonPropsNotValid</Typography>
      ) : (
        <Grid
          p={1}
          component="form"
          container
          direction="column"
          onSubmit={handleSubmit}
          spacing={2}
        >
          {inputFields.map((prop, index) => {
            const fieldValue = newUser[prop.name] || "";
            const isRequired = isFieldRequired(prop) && fieldValue === "";
            const hasRegexError = prop.regex && fieldValue && !validateField(fieldValue, prop.regex);
            const hasError = isRequired || hasRegexError;
            const errorMessage = isRequired 
              ? `${prop.label} is required`
              : hasRegexError 
              ? `${prop.label} format is invalid`
              : "";
            
            return (
            <Grid item key={index}>
              {prop.type === 'select' ? (
                <FormControl 
                  fullWidth 
                  variant="outlined" 
                  size="small" 
                  error={hasError}
                  sx={{
                    "& .MuiInputBase-root.MuiOutlinedInput-root": {
                      borderRadius: "4px",
                    },
                  }}
                >
                  <InputLabel shrink>{prop.label}</InputLabel>
                  <Select
                    value={fieldValue}
                    name={prop.name}
                    onChange={handleChange}
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected) {
                        return <span style={{ color: '#999' }}>{prop.placeholder}</span>;
                      }
                      return selected;
                    }}
                  >
                    {prop.placeholder && (
                      <MenuItem value="" disabled>
                        {prop.placeholder}
                      </MenuItem>
                    )}
                    {prop.options?.map((option, optIndex) => (
                      <MenuItem key={optIndex} value={option.value || option}>
                        {option.label || option}
                      </MenuItem>
                    ))}
                  </Select>
                  {errorMessage && (
                    <FormHelperText sx={{ position: "absolute", right: 0, bottom: -18, zIndex: 10 }}>
                      {errorMessage}
                    </FormHelperText>
                  )}
                </FormControl>
              ) : prop.type === 'radio' ? (
                <FormControl component="fieldset" error={hasError} fullWidth>
                  <FormLabel component="legend">{prop.label}</FormLabel>
                  <RadioGroup
                    row={prop.layout === 'horizontal'}
                    name={prop.name}
                    value={fieldValue}
                    onChange={handleChange}
                  >
                    {prop.options?.map((option, optIndex) => (
                      <FormControlLabel
                        key={optIndex}
                        value={option.value || option}
                        control={<Radio />}
                        label={option.label || option}
                      />
                    ))}
                  </RadioGroup>
                  {errorMessage && (
                    <FormHelperText sx={{ position: "absolute", right: 0, bottom: -18, zIndex: 10 }}>
                      {errorMessage}
                    </FormHelperText>
                  )}
                </FormControl>
              ) : (
                <>
                  <InputLabel shrink>{prop.label}</InputLabel>
                  <TextField
                    sx={{
                      width: "100%",
                      "& .MuiInputBase-root.MuiOutlinedInput-root": {
                        borderRadius: "4px",
                      },
                      "& .MuiFormHelperText-root": {
                        position: "absolute",
                        right: 0,
                        bottom: -18,
                        zIndex: 10,
                      },
                    }}
                    variant="outlined"
                    size="small"
                    type={prop.type}
                    placeholder={prop.placeholder}
                    name={prop.name}
                    onChange={handleChange}
                    error={hasError}
                    helperText={errorMessage}
                    value={fieldValue}
                  />
                </>
              )}
            </Grid>
            );
          })}
          <Grid mt={2} item container justifyContent={"flex-end"}>
            <Button
              type="button"
              variant="outlined"
              color="success"
              onClick={handleSubmit}
            >
              {buttonText ?? "Submit"}
            </Button>
            <Typography color="error">{errorText}</Typography>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}

const mapStateToProps = (state) => {
  return {
    consent: state.user.consent,
    redirectUrl: state.landing.landing.RedirectUrl,
  };
};

export default connect(mapStateToProps, { createUser })(SignUpFormComponent);
